import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Play } from "lucide-react";
import { BIRDS, LOGO } from "@/lib/brand";
import { useLang } from "@/lib/i18n";
import { WhatsAppPill } from "./WhatsAppButton";

const VIDEO_SOURCES = [
  "https://videos.pexels.com/video-files/8543637/8543637-hd_1920_1080_25fps.mp4",
  "https://videos.pexels.com/video-files/8543637/8543637-uhd_2560_1440_25fps.mp4",
  "https://videos.pexels.com/video-files/6985531/6985531-hd_1920_1080_30fps.mp4",
];

const POSTER = "https://images.pexels.com/photos/10221687/pexels-photo-10221687.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1080&w=1920";

export default function Hero() {
  const { t, lang } = useLang();
  const LINES = [
    { text: t("hero.title_line_1"), italic: false },
    { text: t("hero.title_line_2"), italic: true },
    { text: t("hero.title_line_3"), italic: false },
  ];
  const ref = useRef(null);
  const videoRef = useRef(null);
  const [videoReady, setVideoReady] = useState(false);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 180]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.55, 0.85]);
  const birdY = useTransform(scrollYProgress, [0, 1], [0, -260]);
  const birdX = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const birdRot = useTransform(scrollYProgress, [0, 1], [0, -12]);
  const badgeRot = useTransform(scrollYProgress, [0, 1], [0, 60]);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const onCan = () => setVideoReady(true);
    v.addEventListener("canplay", onCan);
    v.play().catch(() => {});
    return () => v.removeEventListener("canplay", onCan);
  }, []);

  return (
    <section id="top" ref={ref} data-testid="hero-section" className="relative h-[100svh] w-full overflow-hidden crop-marks text-black">
      <span className="cm-tr" /><span className="cm-br" />
      <motion.div style={{ scale }} className="absolute inset-0 z-0">
        <img src={POSTER} alt="CMYK ink flow" className="absolute inset-0 w-full h-full object-cover" aria-hidden />
        <video ref={videoRef} className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${videoReady ? "opacity-100" : "opacity-0"}`} autoPlay loop muted playsInline preload="auto" poster={POSTER}>
          {VIDEO_SOURCES.map((src) => <source key={src} src={src} type="video/mp4" />)}
        </video>
        <motion.div style={{ opacity: overlayOpacity }} className="absolute inset-0 bg-[#F4F4F0]" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#F4F4F0]/40 via-transparent to-[#F4F4F0]" />
      </motion.div>
      <div className="pointer-events-none absolute inset-0 z-10 mx-6 md:mx-10 border-x border-black/25" />
      <motion.img src={BIRDS.bird_hero} alt="" aria-hidden style={{ y: birdY, x: birdX, rotate: birdRot }} initial={{ opacity: 0, x: -60, y: 60, rotate: -6 }} animate={{ opacity: 1, x: 0, y: 0, rotate: 0 }} transition={{ duration: 1.6, delay: 0.4, ease: [0.22, 1, 0.36, 1] }} className="pointer-events-none absolute z-[11] right-[-6%] md:right-[-3%] top-[6%] md:top-[4%] w-[62vw] md:w-[46vw] max-w-[820px] select-none drop-shadow-[0_30px_60px_rgba(0,0,0,0.08)]" />
      <motion.div style={{ y }} className="relative z-20 h-full flex flex-col justify-end px-6 md:px-10 pt-28 md:pt-32 pb-14 md:pb-16">
        <div className="max-w-[1440px] mx-auto w-full">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6, duration: 0.8 }} className="flex items-center gap-4 mb-6 md:mb-7">
            <span className="w-10 h-px bg-black" /><span className="font-mono text-[10px] tracking-[0.35em] uppercase">{t("hero.chapter")}</span>
          </motion.div>
          <h1 data-testid="hero-headline" key={lang} className="font-display font-medium text-[13vw] md:text-[8.4vw] lg:text-[8vw] leading-[1.02] tracking-tight max-w-[16ch]">
            {LINES.map((line, i) => (
              <span key={line.text} className="block overflow-visible pb-[0.08em]" aria-label={line.text}>
                <motion.span initial={{ y: "110%" }} animate={{ y: "0%" }} transition={{ duration: 1.05, delay: 0.3 + i * 0.14, ease: [0.22, 1, 0.36, 1] }} className={`block ${line.italic ? "italic" : ""}`}>{line.text}</motion.span>
              </span>
            ))}
          </h1>
          <div className="mt-8 md:mt-10 grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 items-end">
            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1, duration: 0.9 }} className="md:col-span-6 text-base md:text-lg leading-relaxed text-black/80 max-w-[52ch]" data-testid="hero-subheadline">{t("hero.sub")}</motion.p>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.25, duration: 0.9 }} className="md:col-span-4 md:col-start-9 flex flex-col items-start md:items-end gap-4">
              <a href="#studio" data-testid="hero-cta-explore" className="btn-offset"><Play className="w-3.5 h-3.5" />{t("hero.cta")}</a>
              <WhatsAppPill testId="hero-wa-pill" message="Hi Arvind Printers, I saw your website and would like to chat about a print project." />
              <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-black/60">{t("hero.badge_line")}</div>
            </motion.div>
          </div>
        </div>
      </motion.div>
      <motion.a href="#legacy" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.6 }} className="absolute bottom-6 left-6 md:left-10 z-20 font-mono text-[10px] tracking-[0.3em] uppercase flex items-center gap-3 text-black/70"><span className="inline-block w-8 h-px bg-black/70" />Scroll <ArrowDown className="w-3.5 h-3.5 animate-bounce" /></motion.a>
      <motion.div initial={{ opacity: 0, scale: 0.72, y: -12 }} animate={{ opacity: 1, scale: 1, y: 0 }} transition={{ delay: 1.4, duration: 1, ease: [0.22, 1, 0.36, 1] }} className="pointer-events-none hidden md:flex absolute top-24 right-7 lg:right-10 z-30 flex-col items-center gap-2 select-none" data-testid="hero-badge-32">
        <motion.img src={LOGO.badge_32} alt="32 Years of Printing Excellence" style={{ rotate: badgeRot }} className="w-24 lg:w-28 drop-shadow-[0_10px_30px_rgba(0,0,0,0.06)]" />
        <span className="font-sans text-[11px] lg:text-xs font-semibold tracking-[0.12em] uppercase text-black/75">Established 1994</span>
      </motion.div>
    </section>
  );
}
